"use client";

import { ReactNode, useState } from "react";
import toast from "react-hot-toast";
import ConfirmAction from "../components/ConfirmAction";
import Dialog from "../components/Dialog";
import type { PublicationModel } from "../types/models";
import { useQueryClient } from "@tanstack/react-query";
import { usePublicationQuickAction } from "./usePublicationQuickAction";
import { buildQuickActionRequest, captureQuickActionBaseline } from "../components/publicationFlow/quickActions";

// Deleting one scheduled post outright (the calendar popover's "Delete
// post"): a confirm, then the row is gone. The schedule shell never deletes
// on the click — it removes locally and deletes on Save.

export const useDeletePublicationAction = ({
  onDeleted,
}: {
  onDeleted?: (publication: PublicationModel) => void;
} = {}) => {
  const commitQuickAction = usePublicationQuickAction();
  const queryClient = useQueryClient();
  const [pending, setPending] = useState<{
    publication: PublicationModel;
    accountLabel: string;
    baseline: PublicationModel[];
  } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const requestDelete = (publication: PublicationModel, accountLabel: string) => {
    if (isDeleting) return;
    try {
      const baseline = captureQuickActionBaseline(
        queryClient.getQueryData<PublicationModel[]>(["publications"]), [publication],
      );
      setPending({ publication, accountLabel, baseline });
    } catch (error) {
      toast.error((error as Error).message);
    }
  };

  const executeDelete = async (publication: PublicationModel, baseline: PublicationModel[]) => {
    setIsDeleting(true);
    try {
      await toast.promise(commitQuickAction(() => buildQuickActionRequest({ baseline, deletes: [publication] })), {
        loading: "Deleting post...",
        success: "Post deleted",
        error: (error) => error.message ?? "An unknown error occurred",
      });
      onDeleted?.(publication);
    } catch (error) {
      console.error("Delete post failed", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const dialog: ReactNode = (
    <Dialog
      isOpen={pending !== null}
      onClose={() => setPending(null)}
      overlayClassName="!z-[100000]"
      className="max-w-md"
    >
      {pending ? (
        <ConfirmAction
          title="Delete post?"
          description={`This removes the scheduled post for ${pending.accountLabel}. The short stays in your project.`}
          centerText
          confirmLabel="Delete"
          cancelLabel="Cancel"
          onConfirm={() => {
            const { publication, baseline } = pending;
            setPending(null);
            void executeDelete(publication, baseline);
          }}
          onCancel={() => setPending(null)}
        />
      ) : null}
    </Dialog>
  );

  return { requestDelete, isDeleting, isPromptOpen: pending !== null, dialog };
};
